// pages/agent/PreferencesPage.tsx
import React, { useState, useEffect } from "react";
import { Save, Loader2, Phone } from "lucide-react";
import { alertError, alertSuccess } from "../../../../helpers/alertError";
import useAuth from "../../../../hooks/features/auth/useAuth";
import useEntreprises from "../../../../hooks/features/entreprises/useEntreprises";
import { Entreprise } from "../../../../types/features/Entreprise";

interface PreferencesForm {
  call_preference: "softphone" | "hardphone" | "both";
  redirect_phone: string;
}

export const PreferencesPage: React.FC = () => {
  const { user } = useAuth();
  const { updateEntreprise, loading } = useEntreprises();

  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState<PreferencesForm>({
    call_preference: "softphone",
    redirect_phone: "",
  });

  // Charger les données
  useEffect(() => {
    const entreprise = user?.profile as Entreprise;
    if (entreprise) {
      setForm({
        call_preference: entreprise.call_preference || "softphone",
        redirect_phone: entreprise.redirect_phone || "",
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const entreprise = user?.profile as Entreprise;
      
      if (entreprise?.id) {
        const result = await updateEntreprise(entreprise.id, {
          call_preference: form.call_preference,
          redirect_phone: form.redirect_phone,
        });

        if (result) {
          alertSuccess("Préférences enregistrées avec succès !");
        } else {
          alertError("Erreur lors de l'enregistrement");
        }
      }
    } catch (error) {
      alertError("Erreur lors de l'enregistrement");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6 select-none animate-fadeIn">
      {/* Header */}
      <div className="border-b border-slate-100 pb-5">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Préférences d'appels</h1>
        <p className="text-sm text-slate-500">
          Configurez comment vous souhaitez recevoir les appels entrants.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              <h3 className="text-base font-bold text-slate-800">Réception des appels</h3>
            </div>
          </div>

          <div className="p-6 space-y-5">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600">Mode de réception</label>
              <select
                name="call_preference"
                value={form.call_preference}
                onChange={handleChange}
                className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
              >
                <option value="softphone">Softphone (Dashboard)</option>
                <option value="hardphone">Hardphone (Téléphone)</option>
                <option value="both">Les deux</option>
              </select>
              <p className="text-xs text-slate-400">
                {form.call_preference === "softphone" && "Les appels arriveront sur votre dashboard."}
                {form.call_preference === "hardphone" && "Les appels seront redirigés vers votre téléphone."}
                {form.call_preference === "both" && "Les appels arriveront sur votre dashboard ET votre téléphone."}
              </p>
            </div>

            {form.call_preference !== "softphone" && (
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-600">Numéro de redirection</label>
                <input
                  type="text"
                  name="redirect_phone"
                  value={form.redirect_phone}
                  onChange={handleChange}
                  placeholder="+1234567890"
                  className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                />
                <p className="text-xs text-slate-400">Format international : +1 234 567 890</p>
              </div>
            )}
          </div>
        </div>

        {/* Bouton de sauvegarde */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center space-x-2 px-5 py-2.5 text-sm font-semibold bg-primary text-white rounded-xl shadow-md shadow-primary/20 hover:bg-primary/90 transition-all disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>{isSaving ? "Enregistrement..." : "Enregistrer"}</span>
          </button>
        </div>
      </form>
    </div>
  );
};